/**
=========================================================
* Material Dashboard 2 PRO React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// react-router-dom components
import { Link } from 'react-router-dom';

// prop-types is a library for typechecking of props.
import PropTypes from 'prop-types';

// @mui material components
import { Breadcrumbs as MuiBreadcrumbs } from '@mui/material';
import Icon from '@mui/material/Icon';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

import AppRoutes from 'common/AppRoutes';
import { useAppContext } from 'common/AppContext';
import { ROUTES } from 'common/constants/urls';

/**
 * Config-driven breadcrumb rules for each workflow.
 *
 * To add a new flow, add an entry here — no other code changes needed.
 *
 * Fields:
 *  titlePrefix        - page header prefix that identifies this flow
 *  pathSegment        - URL segment that marks the flow root (e.g. "magic-report")
 *                       null → no URL-based segment detection needed
 *  replaceProjectSegment - replace the segment *before* pathSegment with breadcrumbProjectLabel
 *  hideFlowSegments   - hide pathSegment itself + the UUID/ID segment immediately after it
 *  collapseRoutes     - hide all intermediate crumbs (show only home + final title)
 *  formatTitle        - optional fn(title) → string to reformat the final crumb label
 */
const FLOW_CONFIGS = [
  {
    titlePrefix: 'Magic Report:',
    pathSegment: 'magic-report',
    replaceProjectSegment: true,
    hideFlowSegments: true,
    collapseRoutes: false,
    formatTitle: title => {
      const rest = title.substring('Magic Report:'.length).trim();
      return rest ? `Magic Report / ${rest}` : 'Magic Report';
    },
  },
  {
    titlePrefix: 'Process payapp',
    pathSegment: 'process-payapp',
    replaceProjectSegment: true,
    hideFlowSegments: true,
    collapseRoutes: false,
    formatTitle: null,
  },
  {
    titlePrefix: 'Limited Report',
    pathSegment: 'limited-report',
    replaceProjectSegment: true,
    hideFlowSegments: true,
    collapseRoutes: false,
    formatTitle: title => {
      const prefix = 'Limited Report:';
      const rest = title?.startsWith(prefix) ? title.substring(prefix.length).trim() : title;
      return rest ? `Limited Report / ${rest}` : 'Limited Report';
    },
  },
  {
    titlePrefix: 'Invoice',
    pathSegment: null,
    replaceProjectSegment: false,
    hideFlowSegments: false,
    collapseRoutes: true,
    formatTitle: null,
  },
];

/**
 * Build the path string up to and including `element` from the route segments array.
 */
function getPath(routeSegments, element) {
  const path = [''];
  for (const part of routeSegments.slice(0, -1)) {
    path.push(part);
    if (part === element) break;
  }
  return path.join('/');
}

function Breadcrumbs({ icon, title, route, light }) {
  const { breadcrumbProjectLabel } = useAppContext();
  const routeSegments = Array.isArray(route) ? route : route.split('/').filter(Boolean);

  // Match by title prefix first; fall back to path-segment presence for flows where
  // the page header is the report/entity name rather than a fixed prefix (e.g. Magic Report).
  const flowConfig =
    FLOW_CONFIGS.find(c => title?.startsWith(c.titlePrefix)) ??
    FLOW_CONFIGS.find(c => c.pathSegment && routeSegments.includes(c.pathSegment)) ??
    null;

  // Resolve which route segments should be hidden and which segment maps to the project label
  const hiddenSegments = new Set();
  let projectIdSegment = null;

  if (flowConfig?.hideFlowSegments && flowConfig.pathSegment) {
    const flowIdx = routeSegments.indexOf(flowConfig.pathSegment);
    if (flowIdx >= 0) {
      hiddenSegments.add(flowConfig.pathSegment);
      // The UUID/ID segment right after the keyword (e.g. reportId, drawId)
      if (flowIdx + 1 < routeSegments.length) {
        hiddenSegments.add(routeSegments[flowIdx + 1]);
      }
      // The segment before the keyword is the projectId to be humanised
      if (flowConfig.replaceProjectSegment && flowIdx > 0) {
        projectIdSegment = routeSegments[flowIdx - 1];
      }
    }
  }

  const visibleRoutes = flowConfig?.collapseRoutes ? [] : routeSegments.slice(0, -1);
  const displayTitle = flowConfig?.formatTitle ? flowConfig.formatTitle(title) : title.replace('-', ' ');

  // Shared typography style helpers to avoid repetition
  const crumbText = (label, key, cursor = 'default') => (
    <MDTypography
      key={key}
      component='span'
      variant='button'
      fontWeight='regular'
      textTransform='capitalize'
      color={light ? 'white' : 'dark'}
      opacity={light ? 0.8 : 0.5}
      sx={{ lineHeight: 0, cursor }}
    >
      {label}
    </MDTypography>
  );

  return (
    <MDBox mr={{ xs: 0, xl: 8 }}>
      <MuiBreadcrumbs
        sx={{
          '& .MuiBreadcrumbs-separator': {
            color: ({ palette: { white, grey } }) => (light ? white.main : grey[600]),
          },
        }}
      >
        {/* Home icon */}
        <Link to='/'>
          <MDTypography
            component='span'
            variant='body2'
            color={light ? 'white' : 'dark'}
            opacity={light ? 0.8 : 0.5}
            sx={{ lineHeight: 0 }}
          >
            <Icon>{icon}</Icon>
          </MDTypography>
        </Link>

        {/* Intermediate crumbs */}
        {visibleRoutes.map(el => {
          if (hiddenSegments.has(el)) return null;

          const path = getPath(routeSegments, el);
          const matchedRoute = AppRoutes.find(r => r.path === path);

          // Use the human-readable project label for the projectId segment; otherwise
          // use the matched route name, falling back to the raw segment.
          const isProjectSegment = el === projectIdSegment && breadcrumbProjectLabel;
          const label = isProjectSegment ? breadcrumbProjectLabel : matchedRoute?.name ?? el;

          // Project segment in Magic Report / Payapp flows should always link back to the project page
          if (isProjectSegment) {
            const projectPath = `${ROUTES.PROJECTS}/${el}`;
            return (
              <Link to={projectPath} key={el}>
                {crumbText(label, el, 'pointer')}
              </Link>
            );
          }

          // Non-linkable: no matched route, or the route is itself a flow root (Magic/Limited Report)
          const isNonLinkable =
            !matchedRoute?.path ||
            matchedRoute?.name === 'Magic Report' ||
            matchedRoute?.name === 'Limited Report';

          if (isNonLinkable) return crumbText(label, el);

          return (
            <Link to={matchedRoute.path} key={el}>
              {crumbText(label, el, 'pointer')}
            </Link>
          );
        })}

        {/* Final (current page) crumb — not a link */}
        <MDTypography
          variant='button'
          fontWeight='regular'
          textTransform='capitalize'
          color={light ? 'white' : 'dark'}
          sx={{ lineHeight: 0 }}
        >
          {displayTitle}
        </MDTypography>
      </MuiBreadcrumbs>
    </MDBox>
  );
}

Breadcrumbs.defaultProps = {
  light: false,
};

Breadcrumbs.propTypes = {
  icon: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  route: PropTypes.oneOfType([PropTypes.string, PropTypes.array]).isRequired,
  light: PropTypes.bool,
};

export default Breadcrumbs;
